import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Input, Upload, Checkbox, Modal } from 'antd';
import { DeleteOutlined, PaperClipOutlined, EyeOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import PageTitle from '../../components/pageTitle';
import ErrorModal from '../../components/ErrorModal';
import { getLanguageFontWithFallback } from '../../utils/fontUtils';
import { getApiErrorMessage, getRegisterApiErrorMessage } from '../../utils/apiErrorUtils';
import { sendRegisterCode, uploadFile, register, getMe } from '../../api';
import { privacyData } from '../../mockData/privacyData';
import './index.css';
import './index.tablet.css';
import './index.mobile.css';

const REGISTER_STORAGE_KEY = 'registerFormData';

/* ---- Timeline icon images (local cutouts) ---- */
const TL_ICON_IMGS = [
  `${process.env.PUBLIC_URL}/image/网申.png`,
  `${process.env.PUBLIC_URL}/image/专业笔试.png`,
  `${process.env.PUBLIC_URL}/image/面试.png`,
  `${process.env.PUBLIC_URL}/image/在线测评.png`,
];

/* ---- Figma timeline card/dot images ---- */
const TL_IMG_CARD_ACTIVE = `${process.env.PUBLIC_URL}/image/Rectangle_3325.svg`;
const TL_IMG_CARD = `${process.env.PUBLIC_URL}/image/Rectangle_3325_(1).svg`;
const TL_IMG_CARD_ACTIVE_MOBILE = `${process.env.PUBLIC_URL}/image/Rectangle_3325_1.svg`;
const TL_IMG_CARD_MOBILE = `${process.env.PUBLIC_URL}/image/Rectangle_3325_1_(1).svg`;
const TL_IMG_DOT_ACTIVE = `${process.env.PUBLIC_URL}/image/Group_24.svg`;
const TL_IMG_DOT = `${process.env.PUBLIC_URL}/image/Group_58.svg`;

function Register() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'zh';
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const channelId =
    searchParams.get('channelId') || sessionStorage.getItem('channelId') || undefined;

  const isEn = currentLang === 'en';

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 833);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [incompleteFields, setIncompleteFields] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalDesc, setErrorModalDesc] = useState(null);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [licenseFiles, setLicenseFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [emailCodeSendError, setEmailCodeSendError] = useState('');
  const countdownRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 833);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 已登录即表示已提交过报名，禁用提交按钮
  useEffect(() => {
    getMe()
      .then(() => setIsSubmitDisabled(true))
      .catch(() => {});
  }, []);

  const saveFormData = useCallback(() => {
    try {
      const v = form.getFieldsValue();
      delete v.licenseFile;
      sessionStorage.setItem(REGISTER_STORAGE_KEY, JSON.stringify(v));
    } catch (e) {}
  }, [form]);

  const saveFiles = useCallback((files) => {
    try {
      const saved = sessionStorage.getItem(REGISTER_STORAGE_KEY);
      const v = saved ? JSON.parse(saved) : {};
      v.proofFileUrl = files
        .filter((f) => !f.uploading && !f.uploadError && f.uploadUrl)
        .map(({ uid, name, type, uploadUrl }) => ({ uid, name, type, uploadUrl }));
      sessionStorage.setItem(REGISTER_STORAGE_KEY, JSON.stringify(v));
    } catch (e) {}
  }, []);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(REGISTER_STORAGE_KEY);
      if (saved) {
        const v = JSON.parse(saved);
        form.setFieldsValue(v);
        if (Array.isArray(v.proofFileUrl) && v.proofFileUrl.length > 0) {
          setLicenseFiles(
            v.proofFileUrl.map((f) => ({
              ...f,
              url: f.uploadUrl,
              uploading: false,
              uploadError: false,
            }))
          );
        }
        if (v.submitted) {
          setIsSubmitDisabled(true);
        }
      }
    } catch (e) {}
  }, [form]);

  useEffect(() => {
    saveFiles(licenseFiles);
  }, [licenseFiles, saveFiles]);

  useEffect(
    () => () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    },
    []
  );

  const handleGetCode = async () => {
    setEmailCodeSendError('');
    try {
      await form.validateFields(['email']);
    } catch {
      return;
    }
    const email = form.getFieldValue('email');
    const companyName = form.getFieldValue('companyName') || '';
    setIsSendingCode(true);
    try {
      await sendRegisterCode(email, companyName, i18n.language);
    } catch (err) {
      setEmailCodeSendError(
        getApiErrorMessage(err.message, t, 'register.form.emailCode.sendError')
      );
      setIsSendingCode(false);
      return;
    }
    setIsSendingCode(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    setCodeSent(true);
    setCountdown(60);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleFileUpload = async ({ file }) => {
    if (licenseFiles.length >= 5) return false;
    const uid = file.uid || String(Date.now());
    const entry = {
      uid,
      name: file.name,
      size: file.size,
      type: file.type,
      originFileObj: file,
      url: URL.createObjectURL(file),
      uploading: true,
      uploadUrl: null,
      uploadError: false,
    };
    setLicenseFiles((prev) => [...prev, entry]);
    try {
      const res = await uploadFile(file);
      setLicenseFiles((prev) =>
        prev.map((f) => (f.uid === uid ? { ...f, uploading: false, uploadUrl: res.result } : f))
      );
    } catch {
      setLicenseFiles((prev) =>
        prev.map((f) => (f.uid === uid ? { ...f, uploading: false, uploadError: true } : f))
      );
    }
    return false;
  };

  const handleSubmit = async () => {
    const missing = [];
    const values = form.getFieldsValue();
    if (!values.companyName) missing.push(t('register.form.companyName.label'));
    if (!values.contactName) missing.push(t('register.form.contactName.label'));
    if (!values.contactTitle) missing.push(t('register.form.contactTitle.label'));
    if (!values.contactPhone) missing.push(t('register.form.contactPhone.label'));
    if (!values.email) missing.push(t('register.form.email.label'));
    if (!values.emailCode) missing.push(t('register.form.emailCode.label'));
    if (licenseFiles.length === 0) missing.push(t('register.form.license.label'));
    if (!agreePrivacy) missing.push(t('register.form.agree.label'));
    if (missing.length > 0) {
      setIncompleteFields(missing);
      setShowIncompleteModal(true);
      return;
    }
    if (licenseFiles.some((f) => f.uploading)) return;
    if (licenseFiles.some((f) => f.uploadError || !f.uploadUrl)) {
      setErrorModalDesc(null);
      setShowErrorModal(true);
      return;
    }
    const proofFileUrl = JSON.stringify(
      licenseFiles.map((f) => ({ proofFileUrl: f.uploadUrl, proofFileName: f.name }))
    );
    try {
      setIsSubmitting(true);
      await register({
        companyName: values.companyName,
        contactName: values.contactName,
        contactTitle: values.contactTitle,
        contactPhone: values.contactPhone,
        email: values.email,
        verifyCode: values.emailCode,
        proofFileUrl,
        pageLang: i18n.language,
        agreePrivacy: true,
        ...(channelId ? { channelId } : {}),
      });
      setIsSubmitDisabled(true);
      try {
        const saved = sessionStorage.getItem(REGISTER_STORAGE_KEY);
        const v = saved ? JSON.parse(saved) : {};
        v.submitted = true;
        sessionStorage.setItem(REGISTER_STORAGE_KEY, JSON.stringify(v));
      } catch (e) {}
      navigate('/register-success');
      if (countdownRef.current) clearInterval(countdownRef.current);
      setCountdown(0);
      setCodeSent(false);
    } catch (err) {
      setErrorModalDesc(getRegisterApiErrorMessage(err.message, t));
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const timelineSteps = [1, 2, 3, 4].map((n) => ({
    key: n,
    iconSrc: TL_ICON_IMGS[n - 1],
    title: t(`register.timeline.step${n}.title`),
    date: t(`register.timeline.step${n}.date`),
  }));

  const fontFamily = getLanguageFontWithFallback(currentLang);

  return (
    <div className="reg-page">
      <div
        className="reg-hero-bg"
        aria-hidden="true"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/image/弥散背景.png)`,
        }}
      />

      {/* ===== Hero ===== */}
      <section className="reg-section-container reg-hero">
        <div className="reg-hero-body">
          <div className="reg-hero-tag">
            <svg
              className="reg-hero-tag-leaf"
              width="24"
              height="36"
              viewBox="0 0 24 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.1689 28.7531C10.5615 28.753 7.75701 30.6815 6.75559 31.5198C6.51319 31.7228 6.51479 32.0754 6.75246 32.2839C7.86199 33.2569 11.0961 35.7656 15.1689 35.9873C19.3062 36.2125 22.578 33.3417 23.6365 32.284C23.8493 32.0714 23.8416 31.739 23.6118 31.5447C22.6368 30.7204 19.8225 28.7532 15.1689 28.7531Z"
                fill="#93600C"
                fillOpacity="0.9"
              />
              <path
                d="M8.40008 20.2983C4.81984 19.3392 2.36984 20.0883 1.36603 20.5245C1.07397 20.6514 0.98881 20.997 1.17344 21.2564C1.92709 22.3151 4.00152 24.8562 7.17105 25.8852C10.3504 26.9174 13.4782 25.5525 14.6787 24.9162C14.9677 24.7631 15.0411 24.4039 14.8286 24.1553C14.0566 23.2523 12.0229 21.2687 8.40008 20.2983Z"
                fill="#93600C"
                fillOpacity="0.8"
              />
              <path
                d="M8.54285 11.0912C5.90186 9.44946 3.32433 9.924 2.21429 10.2576C1.89675 10.353 1.76872 10.7036 1.93725 10.989C2.49421 11.9323 3.84793 13.95 5.99949 15.4702C8.20622 17.0293 10.6165 17.1 11.6465 17.0441C11.9418 17.0281 12.1493 16.7762 12.1042 16.4841C11.9348 15.3878 11.2539 12.7764 8.54285 11.0912Z"
                fill="#93600C"
                fillOpacity="0.6"
              />
              <path
                d="M10.7997 3.39024C8.74898 1.75937 6.46215 1.87634 5.40104 2.05069C5.06932 2.1052 4.90051 2.44477 5.04244 2.7494C5.4574 3.64005 6.42137 5.41557 8.06267 6.87005C9.80709 8.41591 12.1866 8.43539 13.2236 8.35653C13.5214 8.33389 13.724 8.07383 13.6612 7.78201C13.47 6.89354 12.8396 5.01255 10.7997 3.39024Z"
                fill="#93600C"
                fillOpacity="0.5"
              />
            </svg>
            <span className="reg-hero-tag-text">{t('register.hero.tag')}</span>
            <svg
              className="reg-hero-tag-leaf reg-hero-tag-leaf--flip"
              width="24"
              height="36"
              viewBox="0 0 24 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.1689 28.7531C10.5615 28.753 7.75701 30.6815 6.75559 31.5198C6.51319 31.7228 6.51479 32.0754 6.75246 32.2839C7.86199 33.2569 11.0961 35.7656 15.1689 35.9873C19.3062 36.2125 22.578 33.3417 23.6365 32.284C23.8493 32.0714 23.8416 31.739 23.6118 31.5447C22.6368 30.7204 19.8225 28.7532 15.1689 28.7531Z"
                fill="#93600C"
                fillOpacity="0.9"
              />
              <path
                d="M8.40008 20.2983C4.81984 19.3392 2.36984 20.0883 1.36603 20.5245C1.07397 20.6514 0.98881 20.997 1.17344 21.2564C1.92709 22.3151 4.00152 24.8562 7.17105 25.8852C10.3504 26.9174 13.4782 25.5525 14.6787 24.9162C14.9677 24.7631 15.0411 24.4039 14.8286 24.1553C14.0566 23.2523 12.0229 21.2687 8.40008 20.2983Z"
                fill="#93600C"
                fillOpacity="0.8"
              />
              <path
                d="M8.54285 11.0912C5.90186 9.44946 3.32433 9.924 2.21429 10.2576C1.89675 10.353 1.76872 10.7036 1.93725 10.989C2.49421 11.9323 3.84793 13.95 5.99949 15.4702C8.20622 17.0293 10.6165 17.1 11.6465 17.0441C11.9418 17.0281 12.1493 16.7762 12.1042 16.4841C11.9348 15.3878 11.2539 12.7764 8.54285 11.0912Z"
                fill="#93600C"
                fillOpacity="0.6"
              />
              <path
                d="M10.7997 3.39024C8.74898 1.75937 6.46215 1.87634 5.40104 2.05069C5.06932 2.1052 4.90051 2.44477 5.04244 2.7494C5.4574 3.64005 6.42137 5.41557 8.06267 6.87005C9.80709 8.41591 12.1866 8.43539 13.2236 8.35653C13.5214 8.33389 13.724 8.07383 13.6612 7.78201C13.47 6.89354 12.8396 5.01255 10.7997 3.39024Z"
                fill="#93600C"
                fillOpacity="0.5"
              />
            </svg>
          </div>
          <h1 className={`reg-hero-title ${isEn ? 'reg-hero-title-vertical' : ''}`}>
            <span>
              {t('register.hero.title1')}
              <span className="reg-hero-title-hl">{t('register.hero.titleHighlight')}</span>
            </span>
            <span>{t('register.hero.title2')}</span>
          </h1>
          <p className="reg-hero-subtitle">{t('register.hero.subtitle')}</p>
          <div className="reg-hero-btns">
            <button className="reg-btn-primary" onClick={scrollToForm}>
              {t('register.hero.btnApply')}
            </button>
            <button className="reg-btn-outline" onClick={() => setShowRulesModal(true)}>
              {t('register.hero.btnRules')}
            </button>
            <button className="reg-btn-outline" onClick={() => setShowBenefitsModal(true)}>
              {t('register.hero.btnBenefits')}
            </button>
          </div>
        </div>
      </section>

      {/* ===== Timeline ===== */}
      <section className="reg-section-container reg-tl-section">
        <PageTitle marginBottom="72px">
          <span style={{ fontFamily }}>{t('register.timeline.title')}</span>
        </PageTitle>
        <div className="reg-tl-track">
          {timelineSteps.map(({ key, iconSrc, title, date }, idx) => (
            <React.Fragment key={key}>
              <div className="reg-tl-step">
                <div className={`reg-tl-card${idx === 0 ? ' reg-tl-card--active' : ''}`}>
                  <img
                    className="reg-tl-card-bg"
                    src={
                      idx === 0
                        ? isMobile
                          ? TL_IMG_CARD_ACTIVE_MOBILE
                          : TL_IMG_CARD_ACTIVE
                        : isMobile
                          ? TL_IMG_CARD_MOBILE
                          : TL_IMG_CARD
                    }
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                  <img
                    className="reg-tl-icon-img"
                    src={iconSrc}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                  <p className="reg-tl-title">{title}</p>
                  <p className="reg-tl-date">{date}</p>
                </div>
                <img
                  className={`reg-tl-dot-img ${idx === 0 ? 'reg-tl-dot-img--active' : ''}`}
                  src={idx === 0 ? TL_IMG_DOT_ACTIVE : TL_IMG_DOT}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </React.Fragment>
          ))}
          <div className="reg-tl-connector" />
        </div>
      </section>

      {/* ===== Form Section ===== */}
      <section className="reg-section-container reg-form-section" ref={formRef}>
        <PageTitle marginBottom="64px">
          <span style={{ fontFamily }}>{t('register.form.sectionTitle')}</span>
        </PageTitle>

        <div className="reg-form-outer">
          {/* White card */}
          <div className="reg-form-card">
            <CloseBtn onClick={() => window.history.back()} className="reg-form-card-close" />

            <h2 className="reg-form-card-title">{t('register.form.title')}</h2>
            <p className="reg-form-card-sub">{t('register.form.subtitle')}</p>

            <Form form={form} layout="vertical" onValuesChange={saveFormData} className="reg-form">
              {/* 参选企业名称 */}
              <Form.Item
                name="companyName"
                label={
                  <FieldLabel required={true}>{t('register.form.companyName.label')}</FieldLabel>
                }
              >
                <Input
                  className="reg-input"
                  placeholder={t('register.form.companyName.placeholder')}
                />
              </Form.Item>

              {/* 企业联系人姓名 */}
              <Form.Item
                name="contactName"
                label={<FieldLabel required>{t('register.form.contactName.label')}</FieldLabel>}
              >
                <Input
                  className="reg-input"
                  placeholder={t('register.form.contactName.placeholder')}
                />
              </Form.Item>

              {/* 企业联系人岗位 */}
              <Form.Item
                name="contactTitle"
                label={<FieldLabel required>{t('register.form.contactTitle.label')}</FieldLabel>}
              >
                <Input
                  className="reg-input"
                  placeholder={t('register.form.contactTitle.placeholder')}
                />
              </Form.Item>

              {/* 企业联系人手机 */}
              <Form.Item
                name="contactPhone"
                label={<FieldLabel required>{t('register.form.contactPhone.label')}</FieldLabel>}
              >
                <Input
                  className="reg-input"
                  placeholder={t('register.form.contactPhone.placeholder')}
                />
              </Form.Item>

              {/* 企业联系人邮箱 */}
              <Form.Item
                name="email"
                label={<FieldLabel required>{t('register.form.email.label')}</FieldLabel>}
              >
                <Input className="reg-input" placeholder={t('register.form.email.placeholder')} />
              </Form.Item>

              {/* 邮箱验证码 */}
              <Form.Item
                name="emailCode"
                label={<FieldLabel required>{t('register.form.emailCode.label')}</FieldLabel>}
                extra={
                  emailCodeSendError ? (
                    <span className="reg-code-error">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                      >
                        <path
                          d="M7.33398 0C11.3838 0.000351658 14.667 3.28411 14.667 7.33398C14.6666 11.3836 11.3836 14.6666 7.33398 14.667C3.28411 14.667 0.000351658 11.3838 0 7.33398C0 3.2839 3.2839 0 7.33398 0ZM7.33398 1.33398C4.02028 1.33398 1.33398 4.02028 1.33398 7.33398C1.33434 10.6474 4.02049 13.334 7.33398 13.334C10.6472 13.3336 13.3336 10.6472 13.334 7.33398C13.334 4.02049 10.6474 1.33434 7.33398 1.33398ZM8 10H9V11.333H5.66699V10H6.66699V6.66699H6V5.33301H8V10ZM7.33398 3C7.79381 3.00035 8.16682 3.37314 8.16699 3.83301C8.16699 4.29303 7.79392 4.66664 7.33398 4.66699C6.87375 4.66699 6.5 4.29325 6.5 3.83301C6.50018 3.37292 6.87386 3 7.33398 3Z"
                          fill="#F54336"
                        />
                      </svg>
                      {emailCodeSendError}
                    </span>
                  ) : (
                    codeSent && (
                      <>
                        <div className="reg-code-hint">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M8.00067 0.666748C12.0505 0.6671 15.3337 3.95086 15.3337 8.00073C15.3333 12.0503 12.0502 15.3334 8.00067 15.3337C3.9508 15.3337 0.667039 12.0505 0.666687 8.00073C0.666687 3.95064 3.95058 0.666748 8.00067 0.666748ZM8.00067 2.00073C4.68696 2.00073 2.00067 4.68702 2.00067 8.00073C2.00102 11.3141 4.68718 14.0007 8.00067 14.0007C11.3139 14.0004 14.0003 11.3139 14.0007 8.00073C14.0007 4.68724 11.3141 2.00108 8.00067 2.00073ZM8.66669 10.6667H9.66669V11.9998H6.33368V10.6667H7.33368V7.33374H6.66669V5.99976H8.66669V10.6667ZM8.00067 3.66675C8.4605 3.6671 8.8335 4.03989 8.83368 4.49976C8.83368 4.95978 8.46061 5.33339 8.00067 5.33374C7.54043 5.33374 7.16669 4.95999 7.16669 4.49976C7.16686 4.03967 7.54054 3.66675 8.00067 3.66675Z"
                              fill="#AAAAAA"
                            />
                          </svg>
                          {t('register.form.emailCode.hint')}
                        </div>
                        <div className="reg-code-hint">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M8.00067 0.666748C12.0505 0.6671 15.3337 3.95086 15.3337 8.00073C15.3333 12.0503 12.0502 15.3334 8.00067 15.3337C3.9508 15.3337 0.667039 12.0505 0.666687 8.00073C0.666687 3.95064 3.95058 0.666748 8.00067 0.666748ZM8.00067 2.00073C4.68696 2.00073 2.00067 4.68702 2.00067 8.00073C2.00102 11.3141 4.68718 14.0007 8.00067 14.0007C11.3139 14.0004 14.0003 11.3139 14.0007 8.00073C14.0007 4.68724 11.3141 2.00108 8.00067 2.00073ZM8.66669 10.6667H9.66669V11.9998H6.33368V10.6667H7.33368V7.33374H6.66669V5.99976H8.66669V10.6667ZM8.00067 3.66675C8.4605 3.6671 8.8335 4.03989 8.83368 4.49976C8.83368 4.95978 8.46061 5.33339 8.00067 5.33374C7.54043 5.33374 7.16669 4.95999 7.16669 4.49976C7.16686 4.03967 7.54054 3.66675 8.00067 3.66675Z"
                              fill="#AAAAAA"
                            />
                          </svg>
                          {t('register.form.emailCode.spamHint')}
                        </div>
                      </>
                    )
                  )
                }
              >
                <div className="reg-code-row">
                  <Input
                    className="reg-input reg-code-input"
                    placeholder={t('register.form.emailCode.placeholder')}
                  />
                  <div className="reg-code-divider" />
                  <button
                    type="button"
                    className={`reg-get-code-btn${countdown > 0 || isSendingCode ? ' reg-get-code-btn--wait' : ''}`}
                    onClick={handleGetCode}
                    disabled={countdown > 0 || isSendingCode || isSubmitDisabled}
                  >
                    {isSendingCode
                      ? t('register.form.emailCode.sending')
                      : countdown > 0
                        ? t('register.form.emailCode.resend').replace('{seconds}', countdown)
                        : t('register.form.emailCode.getCode')}
                  </button>
                </div>
              </Form.Item>

              {/* 上传营业执照 */}
              <Form.Item
                label={<FieldLabel required>{t('register.form.license.label')}</FieldLabel>}
              >
                <Upload.Dragger
                  accept=".jpg,.jpeg,.png,.pdf"
                  multiple
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleFileUpload({ file });
                    return false;
                  }}
                  className="reg-upload"
                  style={licenseFiles.length >= 5 ? { display: 'none' } : undefined}
                >
                  <div className="reg-upload-body">
                    <span className="reg-upload-icon" aria-hidden="true">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                      >
                        <path
                          d="M14 3.5V17.5"
                          stroke="#869EBF"
                          strokeWidth="2.33333"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M19.833 9.33333L13.9997 3.5L8.16638 9.33333"
                          stroke="#869EBF"
                          strokeWidth="2.33333"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M24.5 17.5V22.1667C24.5 22.7855 24.2542 23.379 23.8166 23.8166C23.379 24.2542 22.7855 24.5 22.1667 24.5H5.83333C5.21449 24.5 4.621 24.2542 4.18342 23.8166C3.74583 23.379 3.5 22.7855 3.5 22.1667V17.5"
                          stroke="#869EBF"
                          strokeWidth="2.33333"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <p className="reg-upload-text">{t('register.form.license.uploadText')}</p>
                    <p className="reg-upload-hint">{t('register.form.license.hint')}</p>
                    <span className="reg-upload-select-btn">
                      {t('register.form.license.selectFile')}
                    </span>
                  </div>
                </Upload.Dragger>

                {licenseFiles.length > 0 && (
                  <div className="reg-file-list">
                    {licenseFiles.map((f) => (
                      <div key={f.uid} className="reg-file-item">
                        <PaperClipOutlined className="reg-file-clip" />
                        <span className="reg-file-name">{f.name}</span>
                        {f.uploading && (
                          <span className="reg-file-uploading">
                            {t('register.form.license.uploading')}
                          </span>
                        )}
                        {f.uploadError && (
                          <span className="reg-file-upload-error">
                            {t('register.form.license.uploadFailed')}
                          </span>
                        )}
                        {!f.uploading && !f.uploadError && (
                          <button
                            type="button"
                            className="reg-file-btn"
                            onClick={() => {
                              setPreviewFile(f);
                              setPreviewVisible(true);
                            }}
                          >
                            <EyeOutlined />
                          </button>
                        )}
                        <button
                          type="button"
                          className="reg-file-btn reg-file-btn--del"
                          onClick={() => setLicenseFiles((p) => p.filter((x) => x.uid !== f.uid))}
                        >
                          <DeleteOutlined />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Form.Item>

              {/* 确认声明 */}
              <Form.Item className="reg-agree-item">
                <div className="reg-agree-box">
                  <div className="reg-agree-header">
                    <Checkbox
                      checked={agreePrivacy}
                      onChange={(e) => setAgreePrivacy(e.target.checked)}
                      className="reg-agree-checkbox"
                    />
                    <span className="reg-agree-label">
                      {t('register.form.agree.label')}
                      <span className="reg-agree-required-mark">*</span>
                    </span>
                  </div>
                  <p className="reg-agree-desc">
                    {t('register.form.agree.prefix')}
                    <span
                      className="reg-agree-link reg-agree-link--clickable"
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowPrivacyModal(true)}
                      onKeyDown={(e) => e.key === 'Enter' && setShowPrivacyModal(true)}
                    >
                      {t('register.form.agree.link')}
                    </span>
                    {t('register.form.agree.suffix')}
                  </p>
                </div>
              </Form.Item>

              {/* 提交 */}
              <div className="reg-submit-row">
                <button
                  type="button"
                  className={`reg-submit-btn${isSubmitting ? ' reg-submit-btn--loading' : ''}${isSubmitDisabled ? ' reg-submit-btn--registered' : ''}`}
                  onClick={handleSubmit}
                  disabled={isSubmitting || isSubmitDisabled}
                >
                  {isSubmitDisabled ? t('register.form.registered') : t('register.form.submit')}
                  {!isSubmitDisabled && (
                    <span className="reg-arrow-circle">
                      <span
                        className="reg-arrow-icon"
                        style={{ backgroundImage: 'url(/image/黑箭头.png)' }}
                      ></span>
                    </span>
                  )}
                </button>
              </div>
            </Form>
          </div>
        </div>
      </section>

      {/* ===== Modals ===== */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        closeIcon={<CloseBtn />}
        width={800}
        className="reg-preview-modal"
      >
        {previewFile &&
          (previewFile.type === 'application/pdf' ? (
            <iframe
              src={previewFile.url}
              title={previewFile.name}
              style={{ width: '100%', height: '70vh', border: 'none' }}
            />
          ) : (
            <img src={previewFile.url} alt={previewFile.name} style={{ width: '100%' }} />
          ))}
      </Modal>

      <ErrorModal
        open={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setErrorModalDesc(null);
        }}
        title={t('register.error.title')}
        desc1={errorModalDesc ?? t('register.error.desc')}
        btnText={t('register.error.btn')}
      />

      <ErrorModal
        open={showIncompleteModal}
        onClose={() => setShowIncompleteModal(false)}
        title={t('register.incomplete.title')}
        desc1={t('register.incomplete.desc1')}
        desc2={t('register.incomplete.desc2')}
        fieldTitle={t('register.incomplete.pending')}
        fieldContent={incompleteFields.join('、')}
        btnText={t('register.incomplete.btn')}
      />

      <InfoModal
        open={showBenefitsModal}
        onClose={() => setShowBenefitsModal(false)}
        title={t('register.benefits.title')}
      >
        <InfoSection num={1} heading={t('register.benefits.s1.heading')}>
          {t('register.benefits.s1.body')}
        </InfoSection>
        <InfoSection num={2} heading={t('register.benefits.s2.heading')}>
          {t('register.benefits.s2.body')}
        </InfoSection>
        <InfoSection num={3} heading={t('register.benefits.s3.heading')}>
          {t('register.benefits.s3.body')}
        </InfoSection>
        <InfoSection num={4} heading={t('register.benefits.s4.heading')}>
          {t('register.benefits.s4.body')}
        </InfoSection>
      </InfoModal>

      <InfoModal
        open={showRulesModal}
        onClose={() => setShowRulesModal(false)}
        title={t('register.rules.title')}
      >
        <InfoSection num={1} heading={t('register.rules.s1.heading')}>
          <ul className="reg-modal-list">
            <li>{t('register.rules.s1.item1')}</li>
            <li>{t('register.rules.s1.item2')}</li>
            <li>{t('register.rules.s1.item3')}</li>
          </ul>
        </InfoSection>
        <InfoSection num={2} heading={t('register.rules.s2.heading')}>
          {t('register.rules.s2.body')}
        </InfoSection>
        <InfoSection num={3} heading={t('register.rules.s3.heading')}>
          <ul className="reg-modal-list">
            <li>{t('register.rules.s3.item1')}</li>
            <li>{t('register.rules.s3.item2')}</li>
          </ul>
        </InfoSection>
      </InfoModal>

      <InfoModal
        open={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title={(privacyData[currentLang] || privacyData.zh).title}
      >
        <div className="reg-modal-md reg-modal-body-intro">
          <ReactMarkdown components={privacyMdComponents}>
            {(privacyData[currentLang] || privacyData.zh).intro}
          </ReactMarkdown>
        </div>
        {(privacyData[currentLang] || privacyData.zh).sections.map((section, i) => (
          <InfoSection key={i} num={i + 1} heading={section.heading}>
            <div className="reg-modal-md">
              <ReactMarkdown components={privacyMdComponents}>{section.content}</ReactMarkdown>
            </div>
          </InfoSection>
        ))}
      </InfoModal>
    </div>
  );
}

const privacyMdComponents = {
  a: ({ href, children }) => {
    if (href?.startsWith('/')) {
      return (
        <Link to={href} className="reg-modal-md-link" target="_blank" rel="noopener noreferrer">
          {children}
        </Link>
      );
    }
    return (
      <a href={href} className="reg-modal-md-link" target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

function FieldLabel({ required, children }) {
  return (
    <span className="reg-field-label">
      {required && (
        <span className="reg-star" aria-hidden="true">
          *
        </span>
      )}
      {children}
    </span>
  );
}

export default Register;

function InfoModal({ open, onClose, title, children }) {
  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onClose}
      centered
      width={900}
      className="reg-info-modal"
      classNames={{ content: 'reg-info-modal-content' }}
      styles={{
        container: {
          borderRadius: 32,
          padding: 0,
          overflow: 'hidden',
          backgroundColor: '#EFF7FF',
        },
      }}
    >
      <div className="reg-info-modal-header">
        <h3 className="reg-info-modal-title">{title}</h3>
        <CloseBtn onClick={onClose} />
      </div>
      <div
        className="reg-info-modal-background"
        style={{
          backgroundImage: `url("${process.env.PUBLIC_URL}/image/Group_67.png")`,
        }}
      />
      <div className="reg-info-modal-scroll">
        <div className="reg-info-modal-body">{children}</div>
      </div>
    </Modal>
  );
}

function InfoSection({ num, heading, children }) {
  return (
    <div className="reg-info-section">
      <div className="reg-info-section-heading">
        <div className="reg-info-section-num-wrap">
          <span className="reg-info-section-num">{num}</span>
        </div>
        <span className="reg-info-section-heading-text">{heading}</span>
      </div>
      <div className="reg-info-section-body">{children}</div>
    </div>
  );
}

function CloseBtn({ onClick, className }) {
  return (
    <button
      type="button"
      className={`reg-close-btn${className ? ` ${className}` : ''}`}
      aria-label="close"
      onClick={onClick}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M1 1L13 13M13 1L1 13"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}
